import React from 'react';
import { Scale, Globe, FileCheck, MapPinned, MapPin, Building2, Target, Eye, ArrowRight, Heart, Calendar, Users } from 'lucide-react';
import { motion } from 'framer-motion';
import { aboutData } from '../data/mock';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const iconMap = { Scale, Globe, FileCheck, MapPinned };

const About = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-cream">
      <Helmet>
        <title>A Propos - Hope for Peace and Development</title>
        <meta name="description" content="HPD est une organisation feminine sans but lucratif, creee en 2022 en RDC, oeuvrant pour la paix, la justice sociale et le developpement communautaire." />
      </Helmet>

      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-navy overflow-hidden">
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='1'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")` }} />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="uppercase text-xs md:text-sm tracking-[0.2em] font-bold text-forest mb-4 font-sans">Identite Institutionnelle</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold text-white mb-4 tracking-tight">A Propos de HPD</h1>
            <div className="w-16 h-1 bg-forest rounded-full mb-6" />
            <p className="text-lg text-white/70 max-w-2xl font-sans">
              Une organisation engagee pour la paix, la justice sociale et le developpement communautaire
            </p>
          </motion.div>
        </div>
      </section>

      {/* Who we are */}
      <section className="py-24">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-3xl border border-black/5 shadow-sm overflow-hidden">
            <div className="bg-navy p-10 md:p-12 text-white">
              <div className="flex items-center gap-3 mb-6">
                <Heart className="w-8 h-8 text-forest" />
                <h2 className="text-2xl font-serif font-bold">Qui sommes-nous ?</h2>
              </div>
              <p className="text-lg leading-relaxed font-sans text-white/80 mb-6">
                <strong className="notranslate text-white" translate="no">Hope for Peace and Development (HPD)</strong> est une organisation feminine, sans but lucratif, creee en <strong className="text-white">2022</strong> en <strong className="text-white">Republique Democratique du Congo</strong>. HPD oeuvre pour la paix, la justice sociale et le developpement communautaire, en mettant un accent particulier sur les femmes, les jeunes et les personnes vulnerables affectees par les conflits, les crises humanitaires et la pauvrete.
              </p>
              <p className="text-lg leading-relaxed font-sans text-white/80">
                L'organisation agit dans une approche <strong className="text-white">inclusive, participative et sensible au genre</strong>, en collaboration avec les communautes, les institutions publiques, les organisations de la societe civile et les partenaires humanitaires.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-gray-100">
              <div className="p-8 text-center">
                <Calendar className="w-7 h-7 text-navy mx-auto mb-3" />
                <p className="text-sm text-gray-500 mb-1 font-sans">Annee de creation</p>
                <p className="text-2xl font-serif font-bold text-navy">2022</p>
              </div>
              <div className="p-8 text-center">
                <MapPin className="w-7 h-7 text-forest mx-auto mb-3" />
                <p className="text-sm text-gray-500 mb-1 font-sans">Siege</p>
                <p className="text-lg font-serif font-bold text-navy">Bunia, Ituri</p>
                <p className="text-sm text-gray-500 font-sans">Avenue Camp Ndoromo</p>
              </div>
              <div className="p-8 text-center">
                <Users className="w-7 h-7 text-navy mx-auto mb-3" />
                <p className="text-sm text-gray-500 mb-1 font-sans">Beneficiaires</p>
                <p className="text-lg font-serif font-bold text-navy">Femmes, Jeunes & Vulnerables</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Structure */}
      <section className="py-16 bg-warmgray">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-3xl border border-black/5 shadow-sm p-10 md:p-12">
            <div className="flex items-start gap-4 mb-4">
              <Building2 className="w-8 h-8 text-navy flex-shrink-0 mt-1" />
              <div>
                <h3 className="text-2xl font-serif font-bold text-navy mb-4">Notre Structure</h3>
                <p className="text-gray-600 text-base md:text-lg leading-relaxed font-sans">{aboutData.mainText}</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Vision & Mission */}
      <section className="py-24 bg-cream">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-8 mb-16">
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-3xl p-10 border border-black/5 shadow-sm group hover:shadow-md transition-all">
              <div className="w-14 h-14 rounded-xl bg-navy flex items-center justify-center mb-6 group-hover:scale-105 transition-transform">
                <Eye className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-navy mb-4">Notre Vision</h3>
              <p className="text-gray-600 leading-relaxed text-base md:text-lg font-sans">{aboutData.vision}</p>
            </motion.div>
            <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="bg-white rounded-3xl p-10 border border-black/5 shadow-sm group hover:shadow-md transition-all">
              <div className="w-14 h-14 rounded-xl bg-forest flex items-center justify-center mb-6 group-hover:scale-105 transition-transform">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-2xl font-serif font-bold text-navy mb-4">Notre Mission</h3>
              <p className="text-gray-600 leading-relaxed text-base md:text-lg font-sans">{aboutData.mission}</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-24 bg-warmgray">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-navy mb-4 tracking-tight">Nos Valeurs Fondamentales</h2>
            <div className="w-16 h-1 bg-forest rounded-full" />
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {aboutData.values.map((value, index) => {
              const Icon = iconMap[value.icon];
              return (
                <motion.div key={value.id} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.1 }} className="bg-white rounded-2xl p-8 text-center border border-black/5 shadow-sm hover:shadow-md hover:-translate-y-1 transition-all duration-300 group">
                  <div className="w-14 h-14 rounded-xl bg-navy/5 flex items-center justify-center mx-auto mb-6 group-hover:bg-navy transition-colors">
                    <Icon className="w-7 h-7 text-navy group-hover:text-white transition-colors" />
                  </div>
                  <h4 className="text-lg font-serif font-bold text-navy mb-3">{value.title}</h4>
                  <p className="text-gray-600 leading-relaxed text-sm font-sans">{value.description}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Coverage */}
      <section className="py-24 bg-cream">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white rounded-3xl border border-black/5 shadow-sm p-10 md:p-12">
            <div className="text-center mb-10">
              <h2 className="text-3xl font-serif font-bold text-navy mb-4">{aboutData.coverage.title}</h2>
              <p className="text-base md:text-lg text-gray-600 leading-relaxed font-sans">{aboutData.coverage.description}</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {aboutData.coverage.stats.map((stat) => (
                <div key={stat.label} className="bg-cream rounded-2xl p-6 text-center">
                  <p className="text-sm text-gray-500 mb-2 font-sans font-medium">{stat.label}</p>
                  <p className="text-xl font-serif font-bold text-navy">{stat.value}</p>
                </div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 bg-navy relative overflow-hidden">
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-white mb-6 tracking-tight">Travaillons Ensemble</h2>
          <p className="text-lg text-white/70 mb-10 leading-relaxed font-sans max-w-2xl mx-auto">
            Que vous soyez donateur, partenaire ou beneficiaire, nous sommes la pour construire ensemble un avenir meilleur
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button onClick={() => navigate('/#contact')} className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-white text-navy font-semibold hover:bg-gray-100 transition-all shadow-lg group font-sans">
              Contactez-Nous <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </button>
            <button onClick={() => navigate('/don')} className="inline-flex items-center justify-center px-8 py-4 rounded-full border-2 border-white/30 text-white font-semibold hover:bg-white hover:text-navy transition-all backdrop-blur-sm font-sans">
              <Heart className="mr-2 w-5 h-5" /> Faire un Don
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
